import { BaseApiClient } from './base-client';
import type { ApiResponse } from '../../types';
import type {
  WardrobeItem,
  WardrobeItemsResponse,
  WardrobeGenerationResponse,
  WardrobeHistoryResponse,
  ClothingCategory
} from '../../pages/Wardrobe/types';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Wardrobe API - methods for virtual wardrobe and outfit generation
 */
export class WardrobeApi extends BaseApiClient {
  
  // ===== ITEMS API =====
  
  /**
   * Get all wardrobe items for user (including admin presets)
   * @param category - Optional category filter
   */
  async getItems(category?: ClothingCategory, rawTeamId?: string): Promise<ApiResponse<WardrobeItemsResponse>> {
    const teamId = sanitizeTeamId(rawTeamId);
    const params: Record<string, string> = {};
    if (category) params.category = category;
    if (teamId) params.teamId = teamId;
    const response = await this.client.get('/api/wardrobe/items', { params });
    return response.data;
  }

  /**
   * Save a new wardrobe item (after S3 upload)
   */
  async saveItem(data: {
    category: ClothingCategory;
    s3Key: string;
    s3Url: string;
    originalFilename?: string;
    mimeType?: string;
    fileSize?: number;
    teamId?: string;
  }): Promise<ApiResponse<WardrobeItem>> {
    const teamId = sanitizeTeamId(data.teamId);
    const response = await this.client.post('/api/wardrobe/items', { ...data, teamId });
    return response.data;
  }

  /**
   * Delete a wardrobe item
   */
  async deleteItem(itemId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.delete(`/api/wardrobe/items/${itemId}`);
    return response.data;
  }

  // ===== GENERATION API =====

  /**
   * Generate outfit image using selected items
   */
  async generate(data: {
    itemIds: string[];
    poseId?: string;
    aspectRatio?: string;
    teamId?: string;
  }): Promise<ApiResponse<WardrobeGenerationResponse>> {
    const teamId = sanitizeTeamId(data.teamId);
    const response = await this.client.post('/api/wardrobe/generate', { ...data, teamId });
    return response.data;
  }

  // ===== HISTORY API =====

  /**
   * Get generation history
   */
  async getHistory(limit = 50, offset = 0, rawTeamId?: string): Promise<ApiResponse<WardrobeHistoryResponse>> {
    const teamId = sanitizeTeamId(rawTeamId);
    const params: Record<string, string | number> = { limit, offset };
    if (teamId) params.teamId = teamId;
    const response = await this.client.get('/api/wardrobe/history', { params });
    return response.data;
  }

  /**
   * Delete a generation from history
   */
  async deleteGeneration(generationId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.delete(`/api/wardrobe/history/${generationId}`);
    return response.data;
  }

  // ===== ADMIN API =====

  /**
   * Get all admin presets (admin only)
   */
  async adminGetPresets(category?: ClothingCategory): Promise<ApiResponse<WardrobeItem[]>> {
    const params = category ? { category } : {};
    const response = await this.client.get('/api/wardrobe/admin/presets', { params });
    return response.data;
  }

  /**
   * Create admin preset (admin only)
   */
  async adminCreatePreset(data: {
    category: ClothingCategory;
    s3Key: string;
    s3Url: string;
    order?: number;
  }): Promise<ApiResponse<WardrobeItem>> {
    const response = await this.client.post('/api/wardrobe/admin/presets', data);
    return response.data;
  }

  /**
   * Update preset order (admin only)
   */
  async adminUpdatePresetOrder(presetId: string, order: number): Promise<ApiResponse<{ id: string; order: number }>> {
    const response = await this.client.put(`/api/wardrobe/admin/presets/${presetId}/order`, { order });
    return response.data;
  }

  /**
   * Delete admin preset (admin only)
   */
  async adminDeletePreset(presetId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.client.delete(`/api/wardrobe/admin/presets/${presetId}`);
    return response.data;
  }
}

// Export singleton instance
export const wardrobeApi = new WardrobeApi();

